import { cn } from '@/lib/utils';
import ClockStrip, { type ClockState } from './ClockStrip';
import type { RailTab, CenterView } from '@/lib/nflDraftRoomPrefs';

/**
 * Prompt 1: the desktop draft room frame (1a).
 *
 * ── THE min-height:0 TRAP ───────────────────────────────────────────────────
 *
 * All three columns are grid items, and grid items default to `min-height: auto`, which
 * makes them size to their CONTENT and overflow the row rather than clip. The same is true
 * of flex children. So every column carries `min-h-0`, and every inner `flex-1` scroll
 * region needs `min-h-0` on its parent chain too -- the panels do this individually.
 *
 * The outer frame is `h-full overflow-hidden`: nothing scrolls at the document level, which
 * is the whole point of the redesign.
 *
 * SAB-873: `h-full`, not `h-[100dvh]`. The room does NOT own the whole viewport -- it
 * renders below NFLNav, which is `sticky top-0` and takes 3.5rem + 1px of flow, so a room
 * claiming 100dvh made the page 3.5rem taller than the screen and pushed its own bottom edge
 * (rail footer, board legend) below the fold. The CALLER now owns the height and hands it
 * down through a flex column; the room fills what it is given. Consequence worth knowing:
 * `h-full` is `height: 100%`, so a parent with no definite height collapses this to zero.
 * NFLStaging's `flex-1 min-h-0` wrapper is the one caller and provides it. `dvh` still lives
 * there, on the caller's shell, so mobile browser chrome collapsing behaves as before.
 *
 * ── RESPONSIVE ──────────────────────────────────────────────────────────────
 *
 * >=1180px            three columns.
 * 1024-1180           the roster rail is HIDDEN (max-[1180px]:hidden). Not collapsed --
 *                     there is no toggle and no way to bring it back. Making it genuinely
 *                     collapsible is SAB-874.
 * <1024 (touch)       the caller renders MobileDraftRoom instead; this component is
 *                     desktop-only. The switch is useIsMobile() in NFLDraftRoomV2 -- 1024px
 *                     AND touch, iPad forced inside the hook. A narrow desktop window stays
 *                     here, deliberately.
 *
 * SAB-871 corrected two false claims in this block. It used to say the roster rail
 * "collapses behind the clock-strip toggle" at 900-1180 -- ClockStrip really does implement
 * that toggle, but no caller has ever passed `onToggleRail`, so it never rendered and the
 * column is simply display:none. And it used to say a caller rendered the mobile room below
 * 900 -- nothing rendered it at all until SAB-871, and the breakpoint is 1024, not 900.
 */

export interface NFLDraftRoomProps {
  leagueName: string;

  // Clock
  timeLeft: number | null;
  clockState: ClockState;
  clockEyebrow: string;
  clockHeadline: string;
  clockSubline: string;

  // Sabotage
  sabotageCount: number;
  onSabotageClick: () => void;

  centerView: CenterView;
  onCenterViewChange: (v: CenterView) => void;

  ticker: React.ReactNode;
  rosterRail: React.ReactNode;
  centerToolbar: React.ReactNode;
  centerPanel: React.ReactNode;
  rightRail: React.ReactNode;
  topNavRight?: React.ReactNode;

  /** Prompt 10: shown directly beneath the clock strip. */
  disconnected?: boolean;
}

export default function NFLDraftRoom({
  leagueName,
  timeLeft, clockState, clockEyebrow, clockHeadline, clockSubline,
  sabotageCount, onSabotageClick,
  ticker, rosterRail, centerToolbar, centerPanel, rightRail, topNavRight,
  disconnected,
}: NFLDraftRoomProps) {
  return (
    <div
      data-testid="nfl-draft-room"
      className="h-full overflow-hidden flex flex-col bg-background text-foreground"
    >
      <header className="flex-none h-[52px] flex items-center gap-4 border-b border-border bg-background/85 px-4 backdrop-blur-xl">
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md bg-[image:var(--gradient-sabotage)] font-varsity text-[13px] text-[#0B1322]">
          S
        </span>
        <span className="font-display text-sm font-bold text-white truncate">{leagueName}</span>
        <span className="h-[22px] w-px bg-border" />
        <span className="font-display text-[13px] font-bold text-primary">Draft</span>
        <span className="flex-1" />
        {topNavRight}
      </header>

      <ClockStrip
        timeLeft={timeLeft}
        state={clockState}
        eyebrow={clockEyebrow}
        headline={clockHeadline}
        subline={clockSubline}
        sabotageCount={sabotageCount}
        onSabotageClick={onSabotageClick}
        ticker={ticker}
      />

      {disconnected && (
        <div
          data-testid="disconnected-banner"
          className="flex-none border-b border-damage/40 bg-damage/10 px-4 py-2 text-center text-xs font-semibold text-damage"
        >
          Reconnecting… draft actions are paused. The board below is still current as of the
          last update.
        </div>
      )}

      {/* min-h-0 on the grid itself AND on every column. Without it the row grows to fit the
          tallest column and the page scrolls as a whole -- the bug this layout exists to
          eliminate. */}
      {/* SAB-861: min-w-0 on the CENTER column, for the same reason and in the same shape.
          Grid items default to `min-width: auto`, so the `1fr` track (minmax(auto, 1fr))
          could not shrink below the center panel's min-content -- 1721px for a 12-team
          board, whose cells are min-w-[132px]. The track was therefore sized to the board,
          the third column started past the right edge, and an ancestor's overflow-hidden
          clipped it: QUEUE and FEED visible, CHAT gone.
          The board's own overflow-x-auto could not save it. That container was as wide as
          its content (measured 1719/1719), so there was nothing to scroll -- the overflow
          was handed up to the grid instead of being absorbed. With min-w-0 the same
          container measures 1701/394 and scrolls.
          NOT caught by any test, and could not have been: jsdom has no layout engine, so
          every width in this file is 0 there. Verified in a real browser instead -- see the
          measurements in the PR. */}
      <div
        data-testid="draft-room-grid"
        className={cn(
          'flex-1 min-h-0 grid',
          'grid-cols-[236px_1fr_322px]',
          'max-[1180px]:grid-cols-[1fr_322px]',
        )}
      >
        {/* SAB-877: `grid`, so the rail STRETCHES to the row instead of sizing to content.
            Both rails already carry `min-h-0` and an inner `flex-1 min-h-0 overflow-y-auto`
            -- they were written expecting a height-constrained parent. The cell never
            delivered one: a block child with `height: auto` sizes to its content, so a long
            queue or roster made the rail taller than its row, `flex-1` had nothing to
            distribute against, the inner scroller never engaged (scrollHeight === clientHeight
            at every size measured), and everything below the list -- the auto-draft toggle
            included -- was pushed past the room's bottom edge and clipped.
            `grid` makes the rail a grid item, which stretches on both axes by default, so the
            existing `min-h-0` + `overflow-y-auto` finally do what they were written to do.
            Fixed here rather than in the rails because the rails arrive as ReactNode props --
            this component owns the cells, and the cell was what was wrong. */}
        <div className="min-h-0 grid max-[1180px]:hidden">{rosterRail}</div>

        <main className="min-h-0 min-w-0 flex flex-col">
          <div className="flex-none border-b border-border">{centerToolbar}</div>
          {centerPanel}
        </main>

        <div className="min-h-0 grid">{rightRail}</div>
      </div>
    </div>
  );
}

/** The PLAYERS / BOARD segmented control. Sticky selection is the caller's job. */
export function CenterViewToggle({
  view, onChange,
}: { view: CenterView; onChange: (v: CenterView) => void }) {
  return (
    <div
      data-testid="center-view-toggle"
      className="inline-flex rounded-[10px] border border-border bg-muted p-[3px]"
      role="tablist"
    >
      {(['players', 'board'] as CenterView[]).map(v => (
        <button
          key={v}
          role="tab"
          aria-selected={view === v}
          data-testid={`center-view-${v}`}
          onClick={() => onChange(v)}
          className={cn(
            'rounded-[7px] px-4 py-1.5 font-display text-xs font-bold uppercase',
            view === v ? 'bg-primary text-[#12130f]' : 'text-muted-foreground',
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );
}
